// ============================================================================
// Memory Allocator — SharedArrayBuffer Pool
// ============================================================================
// Allocates a single contiguous SharedArrayBuffer for all control regions:
//   [Param Buffer (incl. Error Region)] [Ring Buffer] [Descriptor Table] [Perf Telemetry]
//
// Frame buffers are separate SABs (one per layer/buffer) for independent
// lifecycle management and to avoid reallocating the control region.
import { ERROR_REGION_SIZE, RING_HEADER_SIZE, DESCRIPTOR_TABLE_HEADER_SIZE, BUFFER_ENTRY_SIZE, PERF_REGION_SIZE, BUFFER_FORMAT_BPP, DEFAULT_CONFIG, } from '../types.js';
const align = (n, to) => Math.ceil(n / to) * to;
/**
 * Creates a new buffer ID generator. Each HostBridge instance should
 * own its own generator to avoid ID collisions across instances.
 */
export function createBufferIdGenerator() {
    let nextId = 1;
    return () => nextId++;
}
/**
 * Allocates the control SharedArrayBuffer with all regions laid out contiguously.
 * This SAB is shared between the main thread and the Web Worker.
 *
 * The descriptor table max entries is configurable — set it to the maximum number
 * of simultaneous buffers your application needs. Each entry costs 32 bytes in the
 * control SAB. Default: 64 entries = 2KB.
 */
export function allocateControlMemory(config = DEFAULT_CONFIG, maxDescriptorEntries = 64) {
    const paramSize = Math.max(config.paramBufferSize, ERROR_REGION_SIZE + 512);
    const ringSize = RING_HEADER_SIZE + config.ringBufferCapacity * config.commandSlotSize;
    const descSize = DESCRIPTOR_TABLE_HEADER_SIZE + maxDescriptorEntries * BUFFER_ENTRY_SIZE;
    const paramOffset = 0;
    const ringOffset = align(paramSize, 8);
    const descriptorOffset = ringOffset + align(ringSize, 8);
    const perfOffset = descriptorOffset + align(descSize, 8);
    const totalControlSize = perfOffset + align(PERF_REGION_SIZE, 8);
    // SharedArrayBuffer is required for worker mode. In single-thread mode without
    // COOP/COEP headers, the atomics-shim patches Atomics to work on plain ArrayBuffer.
    const controlSAB = typeof SharedArrayBuffer !== 'undefined'
        ? new SharedArrayBuffer(totalControlSize)
        : new ArrayBuffer(totalControlSize);
    const int32View = new Int32Array(controlSAB);
    const uint8View = new Uint8Array(controlSAB);
    const dataView = new DataView(controlSAB);
    // Initialize ring buffer header
    Atomics.store(int32View, ringOffset >> 2, 0); // write_head = 0
    Atomics.store(int32View, (ringOffset + 4) >> 2, 0); // read_head = 0
    dataView.setUint32(ringOffset + 8, config.ringBufferCapacity, true); // capacity
    // Initialize descriptor table
    Atomics.store(int32View, descriptorOffset >> 2, 0); // buffer_count = 0
    dataView.setUint32(descriptorOffset + 4, 0, true); // reserved
    // Zero error region
    uint8View.fill(0, 0, ERROR_REGION_SIZE);
    return {
        controlSAB,
        paramOffset,
        ringOffset,
        descriptorOffset,
        perfOffset,
        totalControlSize,
        maxDescriptorEntries,
        int32View,
        uint8View,
        dataView,
    };
}
/**
 * Allocates a frame buffer as its own SharedArrayBuffer.
 * The stride is aligned to 16 bytes for SIMD compatibility.
 *
 * Each frame buffer is an independent SAB. This avoids fragmentation entirely —
 * there is no sub-allocation within a single buffer. Creating a buffer allocates
 * a new SAB; destroying it drops the reference. The GC reclaims the memory once
 * all threads release their reference.
 *
 * The refCount tracks how many threads hold a reference (host + worker = 2).
 * Call releaseRef() from each side during destroy; the SAB becomes GC-eligible
 * only when refCount reaches 0.
 */
export function allocateFrameBuffer(bufferId, width, height, format) {
    const bpp = BUFFER_FORMAT_BPP[format] ?? 4;
    const stride = align(width * bpp, 16);
    const byteLength = stride * height;
    const sab = new SharedArrayBuffer(byteLength);
    // Atomic ref count: a tiny 4-byte SAB shared between host and worker.
    // Initialized to 1 (host holds the initial reference).
    const refCountSAB = new SharedArrayBuffer(4);
    Atomics.store(new Int32Array(refCountSAB), 0, 1);
    return {
        bufferId,
        sab,
        data: new Uint8Array(sab),
        width,
        height,
        stride,
        format,
        refCountSAB,
    };
}
/**
 * Atomically increments the reference count.
 * Call when sending SAB to the Worker (host + worker = refCount 2).
 */
export function addRef(handle) {
    Atomics.add(new Int32Array(handle.refCountSAB), 0, 1);
}
/**
 * Atomically decrements the reference count. Returns true if refCount reached 0
 * (meaning the SAB can be dropped from all maps and GC'd).
 */
export function releaseRef(handle) {
    const prev = Atomics.sub(new Int32Array(handle.refCountSAB), 0, 1);
    // prev is the value BEFORE subtraction, so if prev was 1 we're now at 0
    return prev <= 1;
}
/**
 * Read the current reference count (atomic, cross-thread safe).
 */
export function getRefCount(handle) {
    return Atomics.load(new Int32Array(handle.refCountSAB), 0);
}
//# sourceMappingURL=allocator.js.map